import React, { useContext } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { Select, Form, Input, DatePicker, message } from 'antd'
import { ConformityTableType } from './types'
import { UPDATE_CONFORMITY_STATE } from '../../../../graphql/Conformity'
import { CONFORMITY_STATS_QUERY } from '../../../../graphql/Conformity'
import { CONFORMITY_QUERY } from '../../../../graphql/Conformity'
import { VariablesContext } from '../../contexts/variablesContext'
import { SelectedUserContext } from '../../contexts/selectedUserContext'

interface IProps {
  conformity: ConformityTableType
  setConformity: (conformity: ConformityTableType | undefined) => void
  formRef: any
}

const ConformityForm = (props: IProps) => {
  const { conformity, setConformity, formRef } = props
  const { apiParameters } = useContext(VariablesContext)
  const { selectedUser } = useContext(SelectedUserContext)
  const [messageApi, contextHolder] = message.useMessage()

  const displayError = (message: string) => {
    messageApi.open({
      type: 'error',
      content: message,
    })
  }

  const { t } = useTranslation()

  const [updateConformityState] = useMutation(UPDATE_CONFORMITY_STATE, {
    refetchQueries: [
      { query: CONFORMITY_QUERY, variables: { ...apiParameters() } },
      {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: It do the job
        query: selectedUser ? CONFORMITY_STATS_QUERY : undefined,
        variables: { userId: selectedUser },
      },
    ],
  })

  const onFinish = (values: any) => {
    updateConformityState({
      variables: { id: conformity.id, ...values },
    })
      .then(() => setConformity(undefined))
      .catch(response => displayError(response.message))
  }

  const states: Array<string> = [
    'conform',
    'wrong_address',
    'delisted',
    'not_found',
  ]

  const { Option } = Select

  const [form] = Form.useForm<any>()
  const selectedState = Form.useWatch('state', form)
  const siret = Form.useWatch('siret', form)
  const associationId = Form.useWatch('associationId', form)

  const requiredRule = (name: string) => [
    {
      required: true,
      message: t(`conformities:modal.form.${name}.validations`),
    },
  ]

  return (
    <Form
      form={form}
      name="updateConformityForm"
      onFinish={onFinish}
      autoComplete="off"
      ref={formRef}
      layout="vertical"
    >
      {contextHolder}
      <Form.Item
        name="state"
        label={t('conformities:modal.form.state.label')}
        rules={requiredRule('state')}
      >
        <PrettySelect
          allowClear
          onChange={(e: any) => {
            formRef.current!.setFieldsValue({ state: e })
          }}
        >
          {states.map((state: string) => (
            <Option key={state} value={state}>
              {t(`conformities:state.${state}`, {
                address: conformity.address,
              })}
            </Option>
          ))}
        </PrettySelect>
      </Form.Item>
      {['conform', 'wrong_address', 'delisted'].includes(selectedState) && (
        <>
          <Form.Item
            name="siret"
            label={t('conformities:modal.form.siret.label')}
            help={t('conformities:modal.form.siret.tips')}
            rules={[
              {
                required: !associationId,
                message: t(`conformities:modal.form.${name}.validations`),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="associationId"
            label={t('conformities:modal.form.association-id.label')}
            help={t('conformities:modal.form.association-id.tips')}
            rules={[
              {
                required: !siret,
                message: t(`conformities:modal.form.${name}.validations`),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="denomination"
            label={t('conformities:modal.form.denomination.label')}
            help={t('conformities:modal.form.denomination.tips')}
          >
            <Input />
          </Form.Item>
        </>
      )}
      {selectedState === 'conform' && (
        <Form.Item
          name="enterpriseCreatedDate"
          label={t('conformities:modal.form.enterprise-created-date.label')}
          help={t('conformities:modal.form.enterprise-created-date.tips')}
          rules={requiredRule('enterprise-created-date')}
        >
          <DatePicker format={'DD/MM/YYYY'} />
        </Form.Item>
      )}
      {selectedState === 'delisted' && (
        <Form.Item
          name="enterpriseClosedDate"
          label={t('conformities:modal.form.enterprise-closed-date.label')}
          help={t('conformities:modal.form.enterprise-closed-date.tips')}
          rules={requiredRule('enterprise-closed-date')}
        >
          <DatePicker format={'DD/MM/YYYY'} />
        </Form.Item>
      )}
    </Form>
  )
}

const PrettySelect = styled(Select)`
  display: block;
  width: 100%;
`

export default ConformityForm
